import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { fetchPollingSuccess } from "store/actions/polling";
import { setAnomalyAction } from "store/actions/smartInsights";
import { fetchSystemsSuccess } from "store/actions/systemsAndLiveAlarms";
import { setUserAction } from "store/actions/userProfile.js";

import { fetchAnomalyList, fetchSystemsAndAlarms, fetchUserRole, updateLoginTime } from "api/landing";

import getLogger from "utils/logger.util";
import { getItemSession } from "utils/session.js";
import { RefreshKey } from "constants/constantStrings";
import ROUTES_PATH from "constants/pages.constant";
import SYSTEM_STATUS, { ACTUAL_SYSTEM_STATUS } from "constants/systemStatus";

let allActiveAlarms = [];

export default function useSystemsDataHandler() {
  const ALARMGEN_KEY = "ALARMGEN";
  const Logger = getLogger("SystemsDataHandler");
  const params = useParams();
  const { poll } = useSelector(state => state);
  const dispatch = useDispatch();

  useEffect(() => (allActiveAlarms = poll.activeAlarms), [poll.activeAlarms]);

  const getUserRole = async () => {
    let isAuthorized = false;
    let isAdmin = false;
    let isTier1 = false;
    let isTier2 = false;
    let lastVisitedURL = "/";
    try {
      updateLoginTime();
      const userRole = await fetchUserRole();
      isAuthorized = true;
      isAdmin = userRole.data.toLowerCase().includes("admin");
      isTier1 = userRole.data.toLowerCase().includes("tier1");
      isTier2 = userRole.data.toLowerCase().includes("tier2");
      await loadSystemsData();
      lastVisitedURL = getItemSession(RefreshKey.lastVisitedURL);
      if (lastVisitedURL === ROUTES_PATH.DEFAULT || lastVisitedURL === ROUTES_PATH.LOGOUT || lastVisitedURL === ROUTES_PATH.UNUTHORIZED) {
        lastVisitedURL = ROUTES_PATH.HOME;
      }
    } catch (err) {
      lastVisitedURL = null;
      Logger.error("GetUser Role Error: ", err);
    } finally {
      const isAuthenticated = true;
      dispatch(setUserAction({ isAdmin, isTier1, isTier2, isAuthorized, isAuthenticated }));
    }
    return lastVisitedURL;
  };

  const loadSystemsData = async () => {
    let hasSystemsAccess = false;
    try {
      const data = await fetchSystemsAndAlarms();
      let { systems, alarms } = data;
      allActiveAlarms = alarms;
      systems = systems.map(sys => {
        const systemStatus = getSystemState(sys.sysId, sys.connectionStatus, +sys.systemStatus);
        return { ...sys, systemStatus, actualTAG001: +sys.systemStatus };
      });
      hasSystemsAccess = Boolean(systems.length);
      if (hasSystemsAccess) {
        getAnomalyData();
      }
      dispatch(fetchSystemsSuccess(systems));
      dispatch(fetchPollingSuccess(alarms, systems));
    } catch (err) {
      Logger.error("loadSystemsData Error: ", err);
    }
    dispatch(setUserAction({ hasSystemsAccess }));
  };

  const getAnomalyData = async () => {
    const anomalyData = await fetchAnomalyList();
    dispatch(setAnomalyAction(anomalyData.data));
  };

  const getSystemId = () => params.systemId;

  const getSystemState = (system, connectionStatus, Tag001) => {
    let newStatus = SYSTEM_STATUS.STATUS_OFFLINE;
    if (connectionStatus === SYSTEM_STATUS.STATUS_ONLINE) {
      const noOfActiveAlarms = getActiveGenAlarmsCountForSystem(system);
      Logger.log(`getSystemState - noOfActiveGenAlarms in ${system}: ${noOfActiveAlarms}`);
      if (noOfActiveAlarms) {
        newStatus = SYSTEM_STATUS.STATUS_ALARM;
      } else {
        const { NOT_READY, READY, STAND_BY } = ACTUAL_SYSTEM_STATUS;
        const IDLE_STATUS = [NOT_READY, READY, STAND_BY];
        if (IDLE_STATUS.includes(Tag001)) {
          newStatus = SYSTEM_STATUS.SYSTEM_IDLE;
        } else {
          newStatus = SYSTEM_STATUS.STATUS_RUNNING;
        }
      }
    }
    Logger.log(`getSystemState - ${system} Status: ${connectionStatus}, Tag001: ${Tag001}, newStatus: ${newStatus} `);
    return newStatus;
  };

  /**
   * This function will returns count for ALARMGEN alarms for given system from all active alarms in store.
   *
   * @param {*} system
   * @returns
   */
  const getActiveGenAlarmsCountForSystem = system =>
    allActiveAlarms.reduce((count, alarm) => {
      if (alarm.sysId === system && alarm.alarmId.includes(ALARMGEN_KEY)) {
        count++;
      }
      return count;
    }, 0);

  return { getUserRole, loadSystemsData, getSystemId, getSystemState };
}
